import { createContext, useContext, useState, useEffect } from 'react';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const storedToken = localStorage.getItem('accessToken');
    const storedUser = localStorage.getItem('user');
    if (storedToken && storedUser) {
      setToken(storedToken);
      setUser(JSON.parse(storedUser));
    }
    setLoading(false);
  }, []);

  // Called after successful staff/admin login (existing shape)
  const login = (authResponse) => {
    const { accessToken, refreshToken, user: userData } = authResponse;
    setToken(accessToken);
    setUser(userData);
    localStorage.setItem('accessToken', accessToken);
    localStorage.setItem('refreshToken', refreshToken);
    localStorage.setItem('user', JSON.stringify(userData));
  };

  // Called after company login — backend returns full company profile:
  // { token, companyStatus, companyName, contactPersonName, contactEmail,
  //   contactPhone, organizationType, message }
  const companyLogin = (responseData) => {
    const {
      token: companyToken,
      companyStatus,
      companyName,
      contactPersonName,
      contactEmail,
      contactPhone,
      organizationType,
    } = responseData;

    const companyUser = {
      role: 'COMPANY',
      companyStatus,
      companyName,
      contactPersonName,
      contactEmail,
      contactPhone,
      organizationType: organizationType || 'COMPANY',
    };

    setToken(companyToken);
    setUser(companyUser);
    localStorage.setItem('accessToken', companyToken);
    localStorage.setItem('user', JSON.stringify(companyUser));
  };

  const logout = () => {
    setToken(null);
    setUser(null);
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
  };

  const updateUser = (updatedUser) => {
    setUser(updatedUser);
    localStorage.setItem('user', JSON.stringify(updatedUser));
  };

  const isAuthenticated = !!token;
  const isAdmin = user?.role === 'ROLE_ADMIN';
  const isStaff = user?.role === 'ROLE_STAFF';
  const isCompany = user?.role === 'COMPANY';
  const isPartialRegistered = user?.status === 'PARTIAL_REGISTERED';

  return (
    <AuthContext.Provider value={{
      user, token, loading,
      login, companyLogin, logout, updateUser,
      isAuthenticated, isAdmin, isStaff, isCompany, isPartialRegistered
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
